import { useState, useEffect } from 'react';
import clsx from 'clsx';
import { CheckIcon, SunIcon, MoonIcon, ComputerDesktopIcon } from '@heroicons/react/24/outline';

const themes = [
  {
    id: 'blue',
    name: 'Ocean Blue',
    colors: ['#3b82f6', '#2563eb', '#06b6d4'],
    description: 'Professional and trustworthy',
  },
  {
    id: 'purple',
    name: 'Royal Purple',
    colors: ['#a855f7', '#9333ea', '#ec4899'],
    description: 'Creative and elegant',
  },
  {
    id: 'teal',
    name: 'Fresh Teal',
    colors: ['#14b8a6', '#0d9488', '#10b981'],
    description: 'Calm and balanced',
  },
  {
    id: 'emerald',
    name: 'Nature Emerald',
    colors: ['#10b981', '#059669', '#14b8a6'],
    description: 'Growth and harmony',
  },
  {
    id: 'rose',
    name: 'Warm Rose',
    colors: ['#f43f5e', '#e11d48', '#f97316'],
    description: 'Bold and energetic',
  },
];

const darkModeOptions = [
  { id: 'light', label: 'Light', icon: SunIcon },
  { id: 'dark', label: 'Dark', icon: MoonIcon },
  { id: 'system', label: 'System', icon: ComputerDesktopIcon },
];

export function useTheme() {
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('kensafety-theme') || 'blue';
    }
    return 'blue';
  });

  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('kensafety-dark-mode') || 'light';
    }
    return 'light';
  });

  // Apply color theme
  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'blue') {
      root.removeAttribute('data-theme');
    } else {
      root.setAttribute('data-theme', theme);
    }
    localStorage.setItem('kensafety-theme', theme);
  }, [theme]);

  // Apply dark mode
  useEffect(() => {
    const root = document.documentElement;
    localStorage.setItem('kensafety-dark-mode', darkMode);

    const applyDark = (isDark) => {
      if (isDark) {
        root.classList.add('dark');
      } else {
        root.classList.remove('dark');
      }
    };

    if (darkMode === 'dark') {
      applyDark(true);
    } else if (darkMode === 'light') {
      applyDark(false);
    } else {
      // System preference
      const mq = window.matchMedia('(prefers-color-scheme: dark)');
      applyDark(mq.matches);

      const handler = (e) => applyDark(e.matches);
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    }
  }, [darkMode]);

  return { theme, setTheme, themes, darkMode, setDarkMode };
}

export default function ThemeSelector({ compact = false }) {
  const { theme, setTheme, darkMode, setDarkMode } = useTheme();

  if (compact) {
    return (
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          {themes.map((t) => (
            <button
              key={t.id}
              onClick={() => setTheme(t.id)}
              className={clsx(
                'w-8 h-8 rounded-xl transition-all duration-200 relative overflow-hidden',
                'hover:scale-110 active:scale-95',
                theme === t.id && 'ring-2 ring-offset-2 ring-slate-400'
              )}
              style={{
                background: `linear-gradient(135deg, ${t.colors[0]} 0%, ${t.colors[1]} 50%, ${t.colors[2]} 100%)`,
              }}
              title={t.name}
              aria-label={`Select ${t.name} theme`}
              aria-pressed={theme === t.id}
            >
              {theme === t.id && (
                <span className="absolute inset-0 flex items-center justify-center">
                  <CheckIcon className="w-4 h-4 text-white" aria-hidden="true" />
                </span>
              )}
            </button>
          ))}
        </div>
        <div className="w-px h-6 bg-slate-200 dark:bg-slate-600" />
        <button
          onClick={() => setDarkMode(darkMode === 'dark' ? 'light' : 'dark')}
          className="icon-btn"
          title={darkMode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          aria-label={darkMode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {darkMode === 'dark' ? (
            <SunIcon className="w-5 h-5" />
          ) : (
            <MoonIcon className="w-5 h-5" />
          )}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Dark Mode Toggle */}
      <div>
        <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1">Appearance</h3>
        <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">Choose light, dark, or follow system preference</p>
        <div className="flex gap-2">
          {darkModeOptions.map((opt) => (
            <button
              key={opt.id}
              onClick={() => setDarkMode(opt.id)}
              className={clsx(
                'flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all',
                darkMode === opt.id
                  ? 'text-white shadow-md'
                  : 'text-slate-600 dark:text-slate-400 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 hover:border-slate-300'
              )}
              style={darkMode === opt.id ? { background: 'var(--gradient-primary)' } : undefined}
            >
              <opt.icon className="w-4 h-4" />
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Color Theme */}
      <div>
        <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1">Color Theme</h3>
        <p className="text-xs text-slate-500 dark:text-slate-400">Choose a color scheme for your dashboard</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {themes.map((t) => (
          <button
            key={t.id}
            onClick={() => setTheme(t.id)}
            className={clsx(
              'group relative p-4 rounded-xl border-2 transition-all duration-300',
              'hover:scale-[1.02] active:scale-[0.98]',
              theme === t.id
                ? 'border-slate-400 dark:border-slate-500 bg-slate-50 dark:bg-slate-800 shadow-lg'
                : 'border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800/50 hover:border-slate-300 hover:shadow-md'
            )}
          >
            {/* Color Preview */}
            <div className="flex items-center gap-3 mb-3">
              <div
                className="w-12 h-12 rounded-xl shadow-lg"
                style={{
                  background: `linear-gradient(135deg, ${t.colors[0]} 0%, ${t.colors[1]} 50%, ${t.colors[2]} 100%)`,
                }}
              />
              <div className="flex-1 text-left">
                <p className="font-semibold text-slate-800 dark:text-slate-200">{t.name}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">{t.description}</p>
              </div>
              {theme === t.id && (
                <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center">
                  <CheckIcon className="w-4 h-4 text-white" />
                </div>
              )}
            </div>

            {/* Color Swatches */}
            <div className="flex gap-1.5">
              {t.colors.map((color, i) => (
                <div
                  key={i}
                  className="h-2 flex-1 rounded-full"
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>

            {/* Selection Indicator */}
            {theme === t.id && (
              <div
                className="absolute inset-0 rounded-xl pointer-events-none"
                style={{
                  background: `linear-gradient(135deg, ${t.colors[0]}10 0%, ${t.colors[2]}10 100%)`,
                }}
              />
            )}
          </button>
        ))}
      </div>

      {/* Preview Section */}
      <div className="mt-6 p-4 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-600">
        <p className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">Preview</p>
        <div className="flex flex-wrap items-center gap-3">
          <button className="btn btn-primary btn-sm">Primary Button</button>
          <button className="btn btn-secondary btn-sm">Secondary</button>
          <span className="badge badge-primary">Badge</span>
          <span className="badge badge-success">Success</span>
          <div className="flex-1 min-w-[120px]">
            <div className="progress-bar">
              <div className="progress-bar-fill primary" style={{ width: '65%' }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
